import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getModalPortalTarget } from '../lib/portalTarget';
import { useGuardedClose } from '../edit/useGuardedClose';
import { UnsavedChangesModal } from '../edit/UnsavedChangesModal';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  /** true = Body-Content bringt eigenes Scrollen mit (z.B. Iframe fuellt Modal-Innerem). Default false = Modal-Body scrollt. */
  bodyOwnsScroll?: boolean;
  /**
   * Der Inhalt hält ungespeicherte Eingaben: Klick daneben, X und Escape fragen nach
   * (Regel „Dokument-Navigation" R5). Ohne Angabe schließt der Dialog wie bisher sofort.
   */
  dirty?: boolean;
  children: React.ReactNode;
}

export default function AdminPageModal({ open, title, onClose, bodyOwnsScroll = false, dirty = false, children }: Props) {
  const { requestClose, confirmProps } = useGuardedClose({ open, dirty, onClose, label: title });

  if (!open) return null;

  const bodyCls = bodyOwnsScroll
    ? 'flex-1 min-h-0 overflow-hidden'
    : 'flex-1 min-h-0 overflow-auto';

  // Die Rückfrage liegt neben dem Hintergrund, nicht darin (Klicks steigen in React auch
  // durch Portale auf).
  return createPortal(
    <>
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4"
      onClick={requestClose}
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-xl w-[95vw] h-[92vh] max-w-[1600px] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 shrink-0">
          <h3 className="font-semibold text-slate-100">{title}</h3>
          <button
            onClick={requestClose}
            className="text-slate-400 hover:text-slate-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Schliessen"
          >
            <X size={20} />
          </button>
        </div>
        <div className={bodyCls}>
          {children}
        </div>
      </div>
    </div>
    <UnsavedChangesModal {...confirmProps} />
    </>,
    getModalPortalTarget(),
  );
}
